import { useState, useCallback, useMemo } from 'react';
import { supabase, type Tables, type Insertable, type Database } from './supabase';
import { reportError } from './reportError';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TableName = keyof Database['public']['Tables'];

export interface CrudCallbacks<R> {
  onSuccess?: (response: R | null) => void;
  onError?: (error: Record<string, string>) => void;
}

/**
 * Return shape intentionally mirrors the legacy `useApi` hook so existing
 * component consumers don't need changes. Service files swap their internals
 * from Axios → Supabase while components keep destructuring the same object.
 */
export interface CrudResult<TData extends Record<string, unknown>, TResponse> {
  /** Form / filter data — mutable state for building queries */
  data: TData;
  /** Last successful response */
  response: TResponse | null;
  /** Whether a request is in-flight */
  processing: boolean;
  /** Error map keyed by field name (mirrors Rails validation errors) */
  errors: Record<string, string>;
  /** Replace entire data object */
  setData: React.Dispatch<React.SetStateAction<TData>>;
  /** Set a single field (value loosely typed — see useSupabaseList.setFieldData) */
  setFieldData: (field: keyof TData, value: any) => void;
  /** Reset data + errors */
  resetForm: () => void;
  /** Clear errors only */
  clearErrors: () => void;
  /** Set errors manually */
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  /** Set a single error */
  setError: (field: keyof TData, value: string) => void;
  /** Override response manually */
  setResponse: React.Dispatch<React.SetStateAction<TResponse | null>>;
}

// ---------------------------------------------------------------------------
// useSupabaseList — replaces useApi GET with pagination
// ---------------------------------------------------------------------------

export interface ListOptions<T extends TableName> {
  /** Supabase table name */
  table: T;
  /** Columns to select (PostgREST select syntax) */
  select?: string;
  /**
   * Additional filters applied to every query. Receives the current `data`
   * object as the second argument so callers can react to dynamic filter
   * fields (e.g. translate a free-text `name` search into an
   * `or(first_name.ilike,last_name.ilike)` expression instead of the default
   * `.eq('name', value)` — `users` has no `name` column, see UAT R2 #18).
   */
  // The exact PostgREST filter-builder generics are internal to
  // @supabase/postgrest-js (ClientServerOptions / GenericSchema are not
  // exported) and changed arity across versions, so the builder is typed
  // loosely here. The call site already coerces (`baseFilters(query as any)`),
  // and callers simply chain `.eq` / `.ilike` / `.or` / `.is` and return it.
  baseFilters?: (
    query: any,
    data: Record<string, any>,
  ) => any;
  /**
   * Param keys that should NOT be auto-applied as `.eq(key, value)`. Use this
   * when a UI filter field doesn't map to a real column (e.g. a search-string
   * that needs to be expanded across multiple columns by `baseFilters`).
   */
  excludeFromAutoFilter?: string[];
  /** Page size (default 25) */
  pageSize?: number;
  /** Order by column (default 'created_at') */
  orderBy?: string & keyof Tables<T>;
  /** Ascending order? (default false = newest first) */
  ascending?: boolean;
}

export function useSupabaseList<
  T extends TableName,
  TParams extends Record<string, any> = Record<string, any>,
  // Row shape returned by the query. Defaults to the raw table Row, but callers
  // that embed related rows via the `select` string (PostgREST joins) can pass
  // an embed-aware type (e.g. `CourseWithRelations`) so `response` / consumers
  // see the joined keys. This only narrows the TYPE — the runtime query (which
  // already fetches the embeds) is unchanged.
  TRow = Tables<T>,
>(
  defaultParams: TParams,
  options: ListOptions<T>,
) {
  const {
    table,
    select = '*',
    baseFilters,
    pageSize = 25,
    orderBy = 'created_at' as string & keyof Tables<T>,
    ascending = false,
    excludeFromAutoFilter,
  } = options;
  // Memoize so the Set identity is stable across renders (keyed on the
  // option array's contents); otherwise it would be a fresh reference every
  // render and destabilize the `fetchList` useCallback that reads it.
  const excludedKeys = useMemo(
    () => new Set(excludeFromAutoFilter ?? []),
    [excludeFromAutoFilter],
  );

  const [data, setData] = useState<TParams>(defaultParams);
  const [response, setResponse] = useState<TRow[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [page, setPage] = useState(0);

  // `value` stays loosely typed here: tightening it to a per-key generic
  // (`<K extends keyof TParams>(field: K, value: TParams[K])`) surfaces a large
  // backlog of pre-existing caller mismatches (dialogs passing `string | null`
  // into `string | undefined` columns, etc.) that are out of scope for this
  // type-safety pass.
  const setFieldData = useCallback(
    (field: keyof TParams, value: any) => setData((prev) => ({ ...prev, [field]: value })),
    [],
  );
  const setError = useCallback(
    (field: keyof TParams, value: string) => setErrors((prev) => ({ ...prev, [field]: value })),
    [],
  );
  const resetForm = useCallback(() => {
    setData(defaultParams);
    setErrors({});
  }, [defaultParams]);
  const clearErrors = useCallback(() => setErrors({}), []);

  const fetchList = useCallback(
    async (pageOverride?: number, callbacks?: CrudCallbacks<TRow[]>) => {
      setProcessing(true);
      setErrors({});
      try {
        const currentPage = pageOverride ?? page;
        const from = currentPage * pageSize;
        const to = from + pageSize - 1;

        let query = supabase
          .from(table)
          .select(select, { count: 'exact' })
          .range(from, to)
          .order(orderBy, { ascending });

        if (orderBy !== 'id') query = query.order('id', { ascending: true });

        // Apply base filters (e.g., org_id scoping). `data` is now passed in
        // so callers can react to dynamic filter fields excluded from the
        // auto loop below.
        if (baseFilters) {
          query = baseFilters(query as any, data) as any;
        }

        // Apply dynamic param filters (non-empty string/number/array values).
        // Arrays use .in() so callers like { base_role: ['student','parent'] }
        // produce `base_role=in.(student,parent)` instead of the broken
        // `eq.["student","parent"]` that never matches.
        for (const [key, value] of Object.entries(data)) {
          if (value === undefined || value === null || value === '') continue;
          // Pagination + view-state params never map to columns. `per_page`
          // in particular leaked through to PostgREST as `eq.per_page` and
          // surfaced as "column courses.per_page does not exist" on
          // /teacher/courses (SENTRY-COPPER-ELEPHANT-2 after the embed
          // fix unmasked it).
          if (key === 'next_page_token' || key === 'page' || key === 'per_page') continue;
          if (excludedKeys.has(key)) continue;
          if (Array.isArray(value)) {
            if (value.length === 0) continue;
            query = (query as any).in(key, value);
          } else {
            query = (query as any).eq(key, value);
          }
        }

        const { data: rows, error, count } = await query;

        if (error) {
          const msg = reportError({ op: `Load ${table}`, err: error, silent: true, tags: { table: String(table), verb: 'list' } });
          setErrors({ general: msg });
          callbacks?.onError?.({ general: msg });
          return;
        }

        setResponse(rows as unknown as TRow[]);
        setTotalCount(count ?? 0);
        callbacks?.onSuccess?.(rows as unknown as TRow[]);
      } catch (err: unknown) {
        // unexpected throw — not a normal PostgREST error path
        const msg = reportError({ op: `Load ${table}`, err, tags: { table: String(table), verb: 'list' } });
        setErrors({ general: msg });
        callbacks?.onError?.({ general: msg });
      } finally {
        setProcessing(false);
      }
    },
    [table, select, baseFilters, pageSize, orderBy, ascending, data, page, excludedKeys],
  );

  // Backward compatibility: old DynamoDB-backed hooks exposed pageTokens/clearPageTokens.
  // Supabase uses offset pagination, so these are stubs to prevent undefined crashes.
  const pageTokens: string[] = [];
  const clearPageTokens = useCallback(() => {}, []);

  // Real (offset) pagination. The pageTokens API above is a dead stub left from
  // the cursor-based backend; SimplePagination was wired to it, so "Next" never
  // advanced the offset and reloaded the same page (bug e3171fb6). `pageCount`
  // is derived from PostgREST's exact row count; `goToPage` moves to a 0-based
  // page and fetches it.
  const pageCount = Math.max(1, Math.ceil((totalCount || 0) / pageSize));
  const goToPage = useCallback(
    (target: number) => {
      const clamped = Math.min(Math.max(0, target), Math.max(0, pageCount - 1));
      setPage(clamped);
      fetchList(clamped);
    },
    [pageCount, fetchList],
  );

  return {
    data,
    response,
    totalCount,
    processing,
    errors,
    page,
    setPage,
    setData,
    setFieldData,
    setError,
    setErrors,
    setResponse,
    resetForm,
    clearErrors,
    fetchList,
    pageTokens,
    clearPageTokens,
    pageCount,
    goToPage,
  };
}

// ---------------------------------------------------------------------------
// useSupabaseMutation — replaces useApi POST/PUT/DELETE
// ---------------------------------------------------------------------------

export interface MutationOptions<T extends TableName> {
  /** Supabase table name */
  table: T;
  /** Select expression for returned rows (default '*') */
  select?: string;
  /**
   * Bound row id. When provided, `deleteForm(callbacks)` (no id) will use this
   * id. Lets consumer hooks plumb an entity id once and have the inherited
   * mutation use it. Avoids the silent `id=eq.[object Object]` 400s caused by
   * dialogs that called `deleteForm({onSuccess})` thinking the id was bound.
   */
  boundId?: string;
}

export function useSupabaseMutation<
  T extends TableName,
  TBody extends Record<string, any> = Insertable<T>,
>(
  defaultBody: TBody,
  options: MutationOptions<T>,
) {
  const { table, select = '*', boundId } = options;

  const [data, setData] = useState<TBody>(defaultBody);
  const [response, setResponse] = useState<Tables<T> | null>(null);
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // See useSupabaseList.setFieldData above for why `value` stays loose.
  const setFieldData = useCallback(
    (field: keyof TBody, value: any) => setData((prev) => ({ ...prev, [field]: value })),
    [],
  );
  const setError = useCallback(
    (field: keyof TBody, value: string) => setErrors((prev) => ({ ...prev, [field]: value })),
    [],
  );
  const resetForm = useCallback(() => {
    setData(defaultBody);
    setErrors({});
  }, [defaultBody]);
  const clearErrors = useCallback(() => setErrors({}), []);

  /** INSERT a new row */
  const postForm = useCallback(
    async (callbacks?: CrudCallbacks<Tables<T>>) => {
      setProcessing(true);
      setErrors({});
      try {
        const { data: row, error } = await supabase
          .from(table)
          .insert(data as any)
          .select(select)
          .single();

        if (error) {
          const msg = reportError({ op: `Save ${table}`, err: error, tags: { table: String(table), verb: 'insert' } });
          setErrors({ general: msg });
          callbacks?.onError?.({ general: msg });
          return;
        }

        setResponse(row as unknown as Tables<T>);
        callbacks?.onSuccess?.(row as unknown as Tables<T>);
      } catch (err: unknown) {
        const msg = reportError({ op: `Save ${table}`, err, tags: { table: String(table), verb: 'insert' } });
        setErrors({ general: msg });
        callbacks?.onError?.({ general: msg });
      } finally {
        setProcessing(false);
      }
    },
    [table, select, data],
  );

  /** UPDATE an existing row by id */
  const putForm = useCallback(
    async (id: string, callbacks?: CrudCallbacks<Tables<T>>) => {
      setProcessing(true);
      setErrors({});
      try {
        const { data: row, error } = await supabase
          .from(table)
          .update(data as any)
          .eq('id' as any, id)
          .select(select)
          .single();

        if (error) {
          const msg = reportError({ op: `Update ${table}`, err: error, tags: { table: String(table), verb: 'update' } });
          setErrors({ general: msg });
          callbacks?.onError?.({ general: msg });
          return;
        }

        setResponse(row as unknown as Tables<T>);
        callbacks?.onSuccess?.(row as unknown as Tables<T>);
      } catch (err: unknown) {
        const msg = reportError({ op: `Update ${table}`, err, tags: { table: String(table), verb: 'update' } });
        setErrors({ general: msg });
        callbacks?.onError?.({ general: msg });
      } finally {
        setProcessing(false);
      }
    },
    [table, select, data],
  );

  /**
   * DELETE a row by id.
   *
   * Overloaded signature for backward compatibility:
   *   - deleteForm(id, callbacks?)        — explicit id
   *   - deleteForm(callbacks?)            — uses boundId from MutationOptions
   *
   * The shorthand fixes ~17 admin/superadmin delete dialogs that called
   * `deleteForm({onSuccess})` and silently sent DELETE …?id=eq.[object Object].
   */
  const deleteForm = useCallback(
    async (
      idOrCallbacks?: string | CrudCallbacks<Tables<T>>,
      maybeCallbacks?: CrudCallbacks<Tables<T>>,
    ) => {
      const id =
        typeof idOrCallbacks === 'string' ? idOrCallbacks : boundId ?? '';
      const callbacks: CrudCallbacks<Tables<T>> | undefined =
        typeof idOrCallbacks === 'string' ? maybeCallbacks : idOrCallbacks;

      if (!id) {
        const msg = `Cannot delete ${String(table)} without an id`;
        setErrors({ general: msg });
        callbacks?.onError?.({ general: msg });
        return;
      }

      setProcessing(true);
      setErrors({});
      try {
        const { error } = await supabase
          .from(table)
          .delete()
          .eq('id' as any, id);

        if (error) {
          const msg = reportError({ op: `Delete ${table}`, err: error, tags: { table: String(table), verb: 'delete' } });
          setErrors({ general: msg });
          callbacks?.onError?.({ general: msg });
          return;
        }

        setResponse(null);
        callbacks?.onSuccess?.(null);
      } catch (err: unknown) {
        const msg = reportError({ op: `Delete ${table}`, err, tags: { table: String(table), verb: 'delete' } });
        setErrors({ general: msg });
        callbacks?.onError?.({ general: msg });
      } finally {
        setProcessing(false);
      }
    },
    [table, boundId],
  );

  return {
    data,
    response,
    processing,
    errors,
    setData,
    setFieldData,
    setError,
    setErrors,
    setResponse,
    resetForm,
    clearErrors,
    postForm,
    putForm,
    deleteForm,
  };
}

// ---------------------------------------------------------------------------
// useSupabaseGet — fetch a single row by id
// ---------------------------------------------------------------------------

export function useSupabaseGet<T extends TableName, TRow = Tables<T>>(
  table: T,
  select = '*',
  /** Optional pre-bound id — allows callers to use getForm() with no arguments */
  boundId?: string,
) {
  const [response, setResponse] = useState<TRow | null>(null);
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fetchOne = useCallback(
    async (id: string, callbacks?: CrudCallbacks<TRow>) => {
      setProcessing(true);
      setErrors({});
      try {
        const { data: row, error } = await supabase
          .from(table)
          .select(select)
          .eq('id' as any, id)
          .single();

        if (error) {
          const msg = reportError({ op: `Load ${table}`, err: error, silent: true, tags: { table: String(table), verb: 'get' } });
          setErrors({ general: msg });
          callbacks?.onError?.({ general: msg });
          return;
        }

        setResponse(row as unknown as TRow);
        callbacks?.onSuccess?.(row as unknown as TRow);
      } catch (err: unknown) {
        const msg = reportError({ op: `Load ${table}`, err, tags: { table: String(table), verb: 'get' } });
        setErrors({ general: msg });
        callbacks?.onError?.({ general: msg });
      } finally {
        setProcessing(false);
      }
    },
    [table, select],
  );

  /** Zero-arg alias — uses the pre-bound id passed to the hook */
  const getForm = useCallback(
    (callbacks?: CrudCallbacks<TRow>) => {
      if (!boundId) return;
      return fetchOne(boundId, callbacks);
    },
    [fetchOne, boundId],
  );

  return {
    response,
    processing,
    errors,
    setResponse,
    setErrors,
    fetchOne,
    getForm,
  };
}
